import { gql, useMutation, useLazyQuery, useQuery } from "@apollo/client";

const GET_NLP_CONVERSATION_BY_SESSION_ID = gql`
  query GetNlpConversationBySessionId($session_id: String!) {
    getNlpConversationBySessionId(session_id: $session_id) {
      nlp_conversation_id
      user_id
      user_db_id
      session_id
      title
      is_active
      created_on
      updated_on
      messages {
        nlp_message_id
        nlp_conversation_id
        role
        content
        generated_sql
        model_id
        feedback
        created_on
      }
    }
  }
`;

const GET_NLP_CONVERSATIONS = gql`
  query GetNlpConversations($user_id: Int!, $session_id: String, $limit: Int, $offset: Int) {
    getNlpConversations(user_id: $user_id, session_id: $session_id, limit: $limit, offset: $offset) {
      conversations {
        nlp_conversation_id
        user_id
        user_db_id
        session_id
        title
        is_active
        created_on
        updated_on
      }
      hasMore
    }
  }
`;

const GET_NLP_CONVERSATIONS_BY_KEYWORD = gql`
  query GetNlpConversationsByKeyword($user_id: Int!, $keyword: String, $limit: Int, $offset: Int) {
    getNlpConversationsByKeyword(user_id: $user_id, keyword: $keyword, limit: $limit, offset: $offset) {
      conversations {
        nlp_conversation_id
        user_id
        user_db_id
        session_id
        title
        is_active
        created_on
        updated_on
      }
      hasMore
    }
  }
`;

const ADD_NLP_CONVERSATION = gql`
  mutation AddNlpConversation($input: AddNlpConversationInput!) {
    addNlpConversation(input: $input) {
      nlp_conversation_id
      user_id
      user_db_id
      session_id
      title
      is_active
      created_on
      updated_on
      messages {
        nlp_message_id
        nlp_conversation_id
        role
        content
        generated_sql
        created_on
      }
    }
  }
`;

const UPDATE_NLP_CONVERSATION = gql`
  mutation UpdateNlpConversation($input: UpdateNlpConversationInput!) {
    updateNlpConversation(input: $input) {
      nlp_conversation_id
      user_id
      session_id
      title
      is_active
      created_on
      updated_on
    }
  }
`;

const DELETE_NLP_CONVERSATION = gql`
  mutation DeleteNlpConversation($id: Int, $nlp_conversation_id: Int) {
    deleteNlpConversation(id: $id, nlp_conversation_id: $nlp_conversation_id) {
      success
      deleted_id
    }
  }
`;

const ADD_NLP_MESSAGE = gql`
  mutation AddNlpMessage($input: AddNlpMessageMutationInput!) {
    addNlpMessage(input: $input) {
      nlp_message_id
      nlp_conversation_id
      role
      content
      generated_sql
      model_id
      created_on
    }
  }
`;

const UPDATE_NLP_MESSAGE = gql`
  mutation UpdateNlpMessage($input: UpdateNlpMessageInput!) {
    updateNlpMessage(input: $input) {
      nlp_message_id
      role
      content
      generated_sql
      model_id
      feedback
    }
  }
`;

const DELETE_NLP_MESSAGE = gql`
  mutation DeleteNlpMessage($input: DeleteNlpMessageInput!) {
    deleteNlpMessage(input: $input) {
      success
    }
  }
`;

// Map API response to shape the screen expects (chat_id = nlp_conversation_id, session_id, created_at for list)
function mapConversationToChat(conv) {
  if (!conv) return null;
  return {
    ...conv,
    chat_id: conv.nlp_conversation_id,
    id: conv.nlp_conversation_id,
    session_id: conv.session_id ?? null,
    created_at: conv.created_on,
    updated_at: conv.updated_on,
  };
}

/**
 * Hook for Ask FlashDice / NLP conversation persistence (DB-backed).
 * Uses NLP backend: nlp_conversation, nlp_message.
 * Exposes chat_id-style API for the screen; translates to conversation_id under the hood.
 * @param {number|null} userId - When provided, runs GET_NLP_CONVERSATIONS and returns chatList + chatsLoading from cache (no refetch on create).
 */
export const useAskFlashDiceChat = (userId = null) => {
  const [addNlpConvMutation, { loading: addChatLoading, error: addChatError }] =
    useMutation(ADD_NLP_CONVERSATION);
  const [updateNlpConvMutation, { loading: updateChatLoading, error: updateChatError }] =
    useMutation(UPDATE_NLP_CONVERSATION);
  const [deleteNlpConvMutation, { loading: deleteChatLoading, error: deleteChatError }] =
    useMutation(DELETE_NLP_CONVERSATION);
  const [addNlpMessageMutation, { loading: addMessageLoading, error: addMessageError }] =
    useMutation(ADD_NLP_MESSAGE);
  const [updateNlpMessageMutation, { loading: updateMessageLoading, error: updateMessageError }] =
    useMutation(UPDATE_NLP_MESSAGE);
  const [deleteNlpMessageMutation, { loading: deleteMessageLoading, error: deleteMessageError }] =
    useMutation(DELETE_NLP_MESSAGE);

  const [getNlpConvBySessionIdQuery, { loading: getChatBySessionIdLoading, error: getChatBySessionIdError }] =
    useLazyQuery(GET_NLP_CONVERSATION_BY_SESSION_ID);
  const [getNlpConvsQuery, { loading: getChatsLoading, error: getChatsError }] =
    useLazyQuery(GET_NLP_CONVERSATIONS);
  const [getNlpConvsByKeywordQuery, { loading: getChatsByKeywordLoading }] =
    useLazyQuery(GET_NLP_CONVERSATIONS_BY_KEYWORD);

  const { data: conversationsData, loading: conversationsListLoading, refetch: refetchChats, fetchMore, networkStatus } = useQuery(
    GET_NLP_CONVERSATIONS,
    {
      variables: {
        user_id: userId != null ? Number(userId) : 0,
        session_id: null,
        limit: 10,
        offset: 0,
      },
      skip: userId == null,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    }
  );
  const response = conversationsData?.getNlpConversations;
  const chatListFromCache = (response?.conversations ?? []).map(mapConversationToChat);
  const hasMoreConversations = response?.hasMore ?? false;
  const loadingMore = networkStatus === 3; // 3 = fetchMore in progress (Apollo NetworkStatus.fetchMore)

  const loadMoreConversations = () => {
    if (userId == null || !hasMoreConversations || conversationsListLoading) return;
    fetchMore({
      variables: {
        user_id: Number(userId),
        session_id: null,
        limit: 10,
        offset: chatListFromCache.length,
      },
    });
  };

  const loading =
    addChatLoading ||
    updateChatLoading ||
    deleteChatLoading ||
    addMessageLoading ||
    updateMessageLoading ||
    deleteMessageLoading ||
    getChatBySessionIdLoading ||
    getChatsLoading;

  const error =
    addChatError ||
    updateChatError ||
    deleteChatError ||
    addMessageError ||
    updateMessageError ||
    deleteMessageError ||
    getChatBySessionIdError ||
    getChatsError;

  const createChat = async ({ user_id, user_db_id, session_id, title, messages }) => {
    const uid = Number(user_id);
    const queryVars = { user_id: uid, session_id: null };
    const parsedUserDbId =
      user_db_id != null && user_db_id !== "" && !Number.isNaN(Number(user_db_id))
        ? Number(user_db_id)
        : null;
    const { data } = await addNlpConvMutation({
      variables: {
        input: {
          user_id: uid,
          ...(parsedUserDbId != null && { user_db_id: parsedUserDbId }),
          session_id: session_id != null && session_id !== "" ? String(session_id) : "default",
          title: title ?? null,
          messages: (messages || []).map((m) => ({
            role: m.role ?? "user",
            content: m.content ?? "",
          })),
        },
      },
      update(cache, { data: mutationData }) {
        if (!mutationData?.addNlpConversation) return;
        const newConv = mutationData.addNlpConversation;
        const queryVarsWithPagination = { ...queryVars, limit: 10, offset: 0 };
        let existingList = [];
        let hasMore = true;
        try {
          const existing = cache.readQuery({
            query: GET_NLP_CONVERSATIONS,
            variables: queryVarsWithPagination,
          });
          const resp = existing?.getNlpConversations;
          existingList = resp?.conversations ?? [];
          hasMore = resp?.hasMore ?? true;
        } catch (_) {}
        cache.writeQuery({
          query: GET_NLP_CONVERSATIONS,
          variables: queryVarsWithPagination,
          data: {
            getNlpConversations: {
              conversations: [newConv, ...existingList],
              hasMore,
            },
          },
        });
      },
    });
    return mapConversationToChat(data?.addNlpConversation ?? null);
  };

  const updateChat = async (input) => {
    const apiInput = {
      nlp_conversation_id: input.nlp_conversation_id ?? input.chat_id,
      ...(input.session_id !== undefined && { session_id: String(input.session_id) }),
      ...(input.title !== undefined && { title: input.title }),
    };
    const { data } = await updateNlpConvMutation({ variables: { input: apiInput } });
    return mapConversationToChat(data?.updateNlpConversation ?? null);
  };

  const deleteChat = async ({ id, chat_id }) => {
    const pk = id ?? chat_id;
    const { data } = await deleteNlpConvMutation({
      variables: { id: pk, nlp_conversation_id: pk },
    });
    const res = data?.deleteNlpConversation ?? { success: false };
    return {
      success: res.success,
      deleted_id: res.deleted_id,
    };
  };

  const fetchChat = async (session_id) => {
    const { data } = await getNlpConvBySessionIdQuery({
      variables: { session_id: session_id != null && String(session_id).trim() !== "" ? String(session_id).trim() : "default" },
      fetchPolicy: "network-only",
    });
    return mapConversationToChat(data?.getNlpConversationBySessionId ?? null);
  };

  const fetchChatBySessionId = async (session_id) => {
    const { data } = await getNlpConvBySessionIdQuery({
      variables: { session_id: session_id != null && String(session_id).trim() !== "" ? String(session_id).trim() : "default" },
      fetchPolicy: "network-only",
    });
    return mapConversationToChat(data?.getNlpConversationBySessionId ?? null);
  };

  const fetchChats = async (user_id, session_id = null, limit = 10, offset = 0) => {
    if (user_id == null) return [];
    const { data } = await getNlpConvsQuery({
      variables: { user_id: Number(user_id), session_id: session_id ?? null, limit, offset },
      fetchPolicy: "network-only",
    });
    const list = data?.getNlpConversations?.conversations ?? [];
    return list.map(mapConversationToChat);
  };

  const fetchConversationsByKeyword = async (user_id, keyword, limit = 10, offset = 0) => {
    if (user_id == null) return { conversations: [], hasMore: false };
    const { data } = await getNlpConvsByKeywordQuery({
      variables: {
        user_id: Number(user_id),
        keyword: keyword != null && String(keyword).trim() !== "" ? String(keyword).trim() : null,
        limit,
        offset,
      },
      fetchPolicy: "network-only",
    });
    const resp = data?.getNlpConversationsByKeyword;
    const list = resp?.conversations ?? [];
    const hasMore = resp?.hasMore ?? false;
    return { conversations: list.map(mapConversationToChat), hasMore };
  };

  const addMessage = async ({ nlp_conversation_id, role, content, generated_sql, model_id }) => {
    const input = {
      nlp_conversation_id: Number(nlp_conversation_id),
      role: role ?? "user",
      content: content ?? "",
    };
    if (generated_sql != null && generated_sql !== "") input.generated_sql = generated_sql;
    if (model_id != null && model_id !== "") input.model_id = model_id;
    const { data } = await addNlpMessageMutation({
      variables: { input },
    });
    return data?.addNlpMessage ?? null;
  };

  const updateMessage = async (input) => {
    const apiInput = {
      nlp_message_id: input.nlp_message_id ?? input.id,
      content: input.content,
      role: input.role,
      generated_sql: input.generated_sql,
      model_id: input.model_id,
      feedback: input.feedback,
    };
    const { data } = await updateNlpMessageMutation({ variables: { input: apiInput } });
    return data?.updateNlpMessage ?? null;
  };

  const deleteMessage = async (input) => {
    const apiInput = {
      nlp_message_id: input.nlp_message_id ?? input.id,
    };
    const { data } = await deleteNlpMessageMutation({ variables: { input: apiInput } });
    return data?.deleteNlpMessage ?? { success: false };
  };

  return {
    createChat,
    updateChat,
    deleteChat,
    fetchChat,
    fetchChatBySessionId,
    fetchChats,
    fetchConversationsByKeyword,
    refetchChats,
    chatList: chatListFromCache,
    chatsLoading: userId != null ? conversationsListLoading : getChatsLoading,
    searchByKeywordLoading: getChatsByKeywordLoading,
    loadingMore,
    hasMoreConversations,
    loadMoreConversations,
    addMessage,
    updateMessage,
    deleteMessage,
    loading,
    chatLoading: getChatBySessionIdLoading,
    error,
  };
};
